/**
 * Student chapter reader. Route: /portal/courses/:track/:themeSlug/:chapterSlug
 *
 * Uses ChapterReader (anti-copy guarded TipTap renderer) and offers
 * Previous / Next navigation between published chapters in the theme.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ArrowRight } from "@phosphor-icons/react";
import { coursesApi } from "../../api/courses";
import { getTrackLabel } from "../../lib/tracks";
import ChapterReader from "../../components/courses/ChapterReader";

export default function PortalChapterReader() {
  const { t } = useTranslation();
  const { track, themeSlug, chapterSlug } = useParams();
  const [bundle, setBundle] = useState(null);
  const [themeBundle, setThemeBundle] = useState(null); // for prev/next nav
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const [ch, th] = await Promise.all([
        coursesApi.chapter(track, themeSlug, chapterSlug),
        coursesApi.theme(track, themeSlug),
      ]);
      setBundle(ch);
      setThemeBundle(th);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [track, themeSlug, chapterSlug]);

  useEffect(() => {
    load();
    window.scrollTo({ top: 0 });
  }, [load]);

  const navigation = useMemo(() => {
    if (!themeBundle) return { prev: null, next: null };
    const list = themeBundle.chapters || [];
    const idx = list.findIndex((c) => c.slug === chapterSlug);
    return {
      prev: idx > 0 ? list[idx - 1] : null,
      next: idx >= 0 && idx < list.length - 1 ? list[idx + 1] : null,
    };
  }, [themeBundle, chapterSlug]);

  if (loading) return <p className="text-sm text-slate-500">{t("common.loading")}</p>;
  if (error || !bundle) {
    return <p className="text-sm text-red-600">{t("portal.courses.not_found")}</p>;
  }
  const { theme, chapter } = bundle;
  const isEmpty =
    !chapter.content?.content || chapter.content.content.length === 0;

  return (
    <div data-testid="portal-chapter-reader-page" className="space-y-8 max-w-3xl mx-auto">
      <Link
        to={`/portal/courses/${track}/${themeSlug}`}
        data-testid="portal-chapter-back"
        className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-500 hover:text-slate-900"
      >
        <ArrowLeft size={14} weight="bold" />
        {getTrackLabel(track, t)} · {theme.title}
      </Link>

      <header>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
          {t("portal.courses.chapter_kicker")}
        </p>
        <h1 className="mt-2 font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
          {chapter.title}
        </h1>
      </header>

      <article className="rounded-3xl border border-slate-200 bg-white px-6 md:px-10 py-8 md:py-12">
        {isEmpty ? (
          <p className="text-sm text-slate-500">
            {t("portal.courses.empty_chapter")}
          </p>
        ) : (
          <ChapterReader value={chapter.content} />
        )}
      </article>

      {/* Prev / Next */}
      <nav
        data-testid="portal-chapter-nav"
        className="flex items-center justify-between gap-3 pt-4"
      >
        {navigation.prev ? (
          <Link
            to={`/portal/courses/${track}/${themeSlug}/${navigation.prev.slug}`}
            data-testid="portal-chapter-prev"
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-xs font-medium text-slate-700 hover:bg-slate-100"
          >
            <ArrowLeft size={14} weight="bold" />
            {navigation.prev.title}
          </Link>
        ) : (
          <span />
        )}
        {navigation.next ? (
          <Link
            to={`/portal/courses/${track}/${themeSlug}/${navigation.next.slug}`}
            data-testid="portal-chapter-next"
            className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-xs font-medium text-white hover:bg-slate-800"
          >
            {navigation.next.title}
            <ArrowRight size={14} weight="bold" />
          </Link>
        ) : (
          <span />
        )}
      </nav>
    </div>
  );
}
